import React, { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HelpCircleIcon, VolumeIcon, VolumeOffIcon } from '@/component/layout/ShellIcons.tsx'
import { useAlarmSound } from '@/store/AlarmSoundStore.ts'

type IProps = {
    onHover: () => void
    isSheet: boolean
}

const BROWSER_STEPS = [
    { browser: 'topNav.alarmSound.chrome', steps: 'topNav.alarmSound.chromeSteps' },
    { browser: 'topNav.alarmSound.edge', steps: 'topNav.alarmSound.edgeSteps' }
] as const

/**
 * 계정 메뉴의 긴급 알림음 켜고 끄기 줄.
 * 줄 아무 곳을 눌러도 바뀌고, ? 는 마우스 · 키보드 포커스로 보이고 누르면 고정된다. 시트에서는 줄 아래에 펼친다.
 */
const AlarmSoundRow: React.FC<IProps> = ({ onHover, isSheet }) => {
    const { t } = useTranslation()
    const { enabled, setEnabled } = useAlarmSound()
    const [isPinned, setIsPinned] = useState(false)
    const [isPeek, setIsPeek] = useState(false)
    const helpId = useId()
    const showHelp = isPinned || (!isSheet && isPeek)
    const SoundIcon = enabled ? VolumeIcon : VolumeOffIcon

    return (
        <div className={'account-menu__group'} role={'none'} onMouseEnter={onHover}>
            <div
                className={'account-menu__item account-menu__sound'}
                onClick={(e) => {
                    if ((e.target as HTMLElement).closest('.account-menu__help')) return
                    setEnabled(!enabled)
                }}
            >
                <SoundIcon className={'account-menu__icon'} />
                <span className={'account-menu__sound-label'}>{t('topNav.menu.alarmSound')}</span>
                <button
                    type={'button'}
                    tabIndex={-1}
                    className={`account-menu__help ${showHelp ? 'account-menu__help--active' : ''}`}
                    aria-label={t('topNav.alarmSound.help')}
                    aria-expanded={isPinned}
                    onMouseEnter={() => setIsPeek(true)}
                    onMouseLeave={() => setIsPeek(false)}
                    onClick={() => setIsPinned(!isPinned)}
                >
                    <HelpCircleIcon className={'account-menu__help-icon'} />
                </button>
                <button
                    type={'button'}
                    role={'menuitemcheckbox'}
                    aria-checked={enabled}
                    aria-label={t('topNav.menu.alarmSound')}
                    aria-describedby={helpId}
                    data-menu-item
                    className={`account-menu__switch ${enabled ? 'account-menu__switch--on' : ''}`}
                    onFocus={(e) => setIsPeek(e.currentTarget.matches(':focus-visible'))}
                    onBlur={() => setIsPeek(false)}
                >
                    <span className={'account-menu__switch-knob'} />
                </button>
            </div>
            <div
                id={helpId}
                role={'tooltip'}
                className={`account-menu__help-pop ${showHelp ? 'account-menu__help-pop--open' : ''}`}
            >
                <strong>{t('topNav.alarmSound.helpTitle')}</strong>
                <ul>
                    {BROWSER_STEPS.map(({ browser, steps }) => (
                        <li key={browser}>
                            <b>{t(browser)}</b>
                            <span>
                                {t(steps).split(' › ').map((step, index) => (
                                    <React.Fragment key={step}>
                                        {index > 0 && ' '}
                                        <span className={'account-menu__help-step'}>
                                            {index > 0 && '› '}
                                            {step}
                                        </span>
                                    </React.Fragment>
                                ))}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}

export default AlarmSoundRow
